import { useLayoutEffect, useState } from "react";

import { differenceInMilliseconds, secondsToMilliseconds } from "~/tools/date-time";
import type { Time } from "~/tools/date-time/time";
import { onTimeout } from "~/tools/on-timeout";

export const LOADING_DURATION_MS = secondsToMilliseconds(1);

/**
 * The hook ensures that the loading animation is shown for at least `LOADING_DURATION_MS` milliseconds.
 * This prevents flickering of the loading animation when the loading state changes rapidly,
 * and guarantees that the loading animation is visible for at least one full cycle.
 */
export function useFullLoadingAnimation(loading: boolean): boolean {
	const [loadingStartTime, setLoadingStartTime] = useState<Time>();

	useLayoutEffect(() => {
		if (loading && loadingStartTime == null) {
			setLoadingStartTime(Date.now());
		} else if (!loading && loadingStartTime != null) {
			const duration = differenceInMilliseconds(Date.now(), loadingStartTime);
			const remaining = LOADING_DURATION_MS - (duration % LOADING_DURATION_MS);

			return onTimeout(() => {
				setLoadingStartTime(undefined);
			}, remaining);
		}
	}, [loading, loadingStartTime]);

	return loading || loadingStartTime != null;
}
